import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/atoms/button";
import { Icon } from "@/atoms/icon";
import { FactList, FactRow } from "@/components/fact-row";
import { PriceDisplay } from "@/components/price-display";
import { TotalsTotal } from "@/components/totals";
import { dueLine } from "@/utils/date";
import { everyPhrase } from "@/utils/interval";
import { formatTableMoney, fromCents, splitDisplayMoney } from "@/utils/money";
import type { PublicInvoiceDetails } from "@/data/invoice";

/**
 * What is owed, by when, and for what (template checkout 1.4, invoice variant; spec invoice 1.0).
 *
 * The answer is the first thing on the page: the amount at display size and the moment line
 * under it. Everything the old page hid behind a "View invoice and payment details" modal is
 * open here, as fact rows in reading order, because a person opening an invoice link has exactly
 * one question and it should not take a click.
 *
 * The document itself is not rendered inline: invoice 1.0's 390 layout forbids it, and the PDF
 * sits behind one tertiary control instead.
 */

export interface InvoiceSummaryProps {
  invoice: PublicInvoiceDetails;
  /** Whether the h1 should take focus on load; the receipt takes it when the invoice is paid. */
  focusHeading?: boolean;
}

export function InvoiceSummary({
  invoice,
  focusHeading = true,
}: InvoiceSummaryProps) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusHeading) headingRef.current?.focus();
  }, [focusHeading]);

  const { invoice: doc, lineItems, customer, subscription } = invoice;
  const currency = doc.currency;
  const money = (cents: number) => formatTableMoney(fromCents(cents), currency);
  const { figure, unit } = splitDisplayMoney(
    fromCents(doc.amountDue),
    currency,
  );

  /* The number is the eyebrow, in the mono face, and it is ALWAYS above the h1 when there is
     one: a reference is read out loud and typed back, which is the whole reason the system has a
     mono face. The h1 is what the invoice is FOR, in the order the brief gives, and "Invoice" is
     the last resort rather than the usual answer. */
  const subject =
    doc.description?.trim() ||
    subscription?.productName ||
    lineItems[0]?.description ||
    null;
  const heading = subject ?? t("gymkeePay.invoice.fallbackTitle");
  /* The eyebrow carries the reference. When the h1 has fallen back to the word "Invoice" the
     eyebrow drops the word and prints the number alone, so the page does not say "Invoice"
     twice in two faces. */
  const numberLine = doc.number
    ? subject
      ? t("gymkeePay.invoice.numberLine", { number: doc.number })
      : doc.number
    : null;

  const due = dueLine(doc.dueDate);
  const hasTax = doc.total !== doc.subtotal;
  const included = invoice.product?.highlights?.included ?? [];
  const billedName = customer.name?.trim();
  const billedEmail = customer.email?.trim();
  const businessName = invoice.business.name?.trim();

  return (
    <section className="gap-gk-6 flex flex-col">
      <div className="gap-gk-2 flex flex-col">
        {numberLine && (
          <p className="gk-mono text-[var(--gk-text-tertiary)]">{numberLine}</p>
        )}
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="ds-pay-lede text-[24px] leading-[1.2] font-semibold text-[var(--gk-text-primary)]"
        >
          {heading}
        </h1>
      </div>

      <PriceDisplay
        figure={figure}
        unit={unit}
        cadence={
          due ? (
            <span
              style={{
                color:
                  due.tone === "danger"
                    ? "var(--gk-semantic-danger-text)"
                    : "var(--gk-text-secondary)",
              }}
            >
              {due.kind === "today"
                ? t("gymkeePay.invoice.dueToday")
                : due.kind === "overdue"
                  ? t("gymkeePay.invoice.overdueSince", { date: due.date })
                  : t("gymkeePay.invoice.dueOn", { date: due.date })}
            </span>
          ) : null
        }
      />

      {/* What is invoiced, and only when the payload itemises it. With no lines the 56 figure
          above IS the answer, and a "Total due" row repeating it would be the price said twice
          at two sizes (checkout 1.4 doNot 4). The total row is skipped again when a single line
          already equals it. */}
      {lineItems.length > 0 && (
        <div className="gap-gk-3 mt-gk-2 flex flex-col">
          <h2 className="text-[20px] font-semibold text-[var(--gk-text-primary)]">
            {t("gymkeePay.invoice.whatIsInvoiced")}
          </h2>
          <FactList>
            {lineItems.map((item, index) => (
              <FactRow
                key={`${item.description ?? "line"}-${index}`}
                label={item.description || t("gymkeePay.invoice.fallbackTitle")}
                sublabel={
                  item.quantity > 1
                    ? t("gymkeePay.invoice.qtyUnit", {
                        qty: item.quantity,
                        unit: money(item.unitAmount),
                      })
                    : undefined
                }
                value={money(item.amount)}
                numeric
              />
            ))}
            {hasTax && (
              <FactRow
                label={t("gymkeePay.invoice.subtotalLine")}
                value={money(doc.subtotal)}
                numeric
              />
            )}
            {hasTax && (
              <FactRow
                label={t("gymkeePay.invoice.taxLine")}
                value={money(doc.total - doc.subtotal)}
                numeric
              />
            )}
          </FactList>
          {(lineItems.length > 1 || hasTax) && (
            <TotalsTotal
              label={t("gymkeePay.invoice.totalDue")}
              amount={money(doc.amountDue)}
            />
          )}
        </div>
      )}

      {doc.invoicePdf && (
        /* The document, behind one tertiary control (rule 134), and the control is words: gate
           7.3 and button-tertiary 1.4's last doNot both keep a glyph out of a button, and the
           three words already say what the file is. */
        <Button
          variant="tertiary"
          href={doc.invoicePdf}
          target="_blank"
          className="ds-pay-hand self-start"
        >
          {t("gymkeePay.invoice.downloadInvoice")}
        </Button>
      )}

      {/* Who the invoice is for and who it is from, and only where the payload knows. Two rows
          of en dashes is not a fact list, it is noise on a page about money; the gap is a
          backend one and it is named in the report, not papered over here. */}
      {(billedName || billedEmail || businessName) && (
        <FactList>
          {(billedName || billedEmail) && (
            <FactRow
              label={t("gymkeePay.invoice.billedTo")}
              value={billedName || billedEmail}
              subvalue={billedName && billedEmail ? billedEmail : undefined}
            />
          )}
          {businessName && (
            <FactRow label={t("gymkeePay.invoice.from")} value={businessName} />
          )}
        </FactList>
      )}

      {subscription && (
        <p className="text-[13px] leading-[1.5] text-[var(--gk-text-tertiary)]">
          {t("gymkeePay.invoice.subscriptionNote", {
            product: subscription.productName,
            cadence:
              everyPhrase(t, {
                interval: subscription.interval,
                intervalCount: subscription.intervalCount,
              }) ?? "",
          })}
        </p>
      )}

      {included.length > 0 && (
        <div className="gap-gk-2 flex flex-col">
          <h2 className="text-[20px] font-semibold text-[var(--gk-text-primary)]">
            {t("gymkeePay.invoice.whatYouGet")}
          </h2>
          <ul className="flex flex-col">
            {included.map((line) => (
              <li
                key={line.text}
                className="gap-gk-3 flex min-h-[40px] items-center text-[14px] text-[var(--gk-text-secondary)]"
              >
                <Icon
                  name="set-done"
                  size={20}
                  style={{ color: "var(--gk-text-primary)", flexShrink: 0 }}
                />
                <span className="min-w-0">{line.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
